import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `t` + locale de fechas atados a un idioma EXPLÍCITO en vez del detectado por
 * la app. Lo usa el wizard de reserva dentro del viewer `.tap`: el visitante
 * eligió un idioma para el venue, y sin esto el panel quedaba mitad en ese
 * idioma (chrome del tap, chips del menú) y mitad en el del navegador.
 *
 * Los bundles de traducción son lazy por idioma (ver `translations/i18n.ts`),
 * así que primero se pide el del idioma pedido; hasta que llega —o para
 * siempre, si el venue usa un idioma custom que la app no traduce— se sigue
 * con el idioma de la app: nunca claves crudas.
 *
 * Sin `lang` devuelve exactamente el comportamiento previo (útil para que un
 * consumidor lo use siempre y el caller decida).
 */
export function useLangBoundT(lang?: string) {
  const { t: tApp, i18n } = useTranslation();

  // `supportedLngs` es `false` cuando no se configura — de ahí el Array.isArray.
  const supported = i18n.options.supportedLngs;
  const appSupports =
    !!lang && Array.isArray(supported) && supported.includes(lang);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!appSupports || !lang) return;
    let alive = true;
    i18n.loadLanguages(lang).then(() => {
      if (alive) setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, [appSupports, lang, i18n]);

  const t = useMemo(
    () => (appSupports && loaded && lang ? i18n.getFixedT(lang) : tApp),
    [appSupports, loaded, lang, i18n, tApp],
  );

  /**
   * Locale para `Intl`: cualquier BCP-47 válido sirve aunque no tengamos
   * traducciones (un menú en alemán merece fechas en alemán), pero un código
   * inventado haría throw, así que se valida antes de usarlo.
   */
  const dateLocale = useMemo(() => {
    if (!lang) return i18n.language;
    try {
      new Intl.DateTimeFormat(lang);
      return lang;
    } catch {
      return i18n.language;
    }
  }, [lang, i18n.language]);

  return { t, dateLocale };
}
